<template>
  <div
    class="tour-cards-widget"
    :class="[`style-${widget.cardStyle || 'default'}`, widgetClasses]"
    :style="widgetStyles"
  >
    <!-- Widget Title -->
    <div v-if="widget.title" class="widget-header mb-4">
      <h3 class="widget-title">{{ widget.title }}</h3>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state text-center py-4">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Φόρτωση...</span>
      </div>
      <p class="mt-2 text-muted">Φόρτωση εκδρομών...</p>
    </div>

    <!-- Tours Grid - Default Style -->
    <div v-else-if="tours.length > 0" class="tours-grid">
      <div class="tours-grid-layout" :style="{ '--tour-columns': widget.columns || 3 }">
        <div v-for="tour in tours" :key="tour.id" class="tour-grid-item">
          <article class="tour-card h-100">
            <!-- Tour Image -->
            <div v-if="widget.showImage !== false && tour.featured_image" class="tour-image-wrapper">
              <div class="tour-image">
                <img 
                  :src="getTourImageUrl(tour.featured_image)"
                  :alt="stripHtml(tour.title)"
                  loading="lazy"
                  decoding="async"
                  @error="handleImageError"
                />
                
                <!-- Action Buttons Overlay -->
                <div class="tour-actions-overlay">
                  <button 
                    @click.prevent="addToWishlist(tour)"
                    class="action-btn wishlist-btn"
                    :class="{ 'active': isInWishlist(tour.id) }"
                    :title="isInWishlist(tour.id) ? 'Αφαίρεση από Wishlist' : 'Προσθήκη στη Wishlist'"
                  >
                    <i class="bi" :class="isInWishlist(tour.id) ? 'bi-heart-fill' : 'bi-heart'"></i>
                  </button>
                  
                  <button 
                    @click.prevent="openQuickView(tour)"
                    class="action-btn quick-view-btn"
                    title="Γρήγορη Προβολή"
                  >
                    <i class="bi bi-eye"></i>
                  </button>
                </div>
              </div>
              
              <div v-if="widget.showCategory !== false && tour.category_name" class="tour-category-badge">
                {{ tour.category_name }}
              </div>
            </div>

            <!-- Tour Content -->
            <div class="tour-content">
              <h4 class="tour-title text-truncate" :title="stripHtml(tour.title)">
                <router-link :to="`/tours/${tour.slug}`" class="tour-title-link">
                  {{ stripHtml(tour.title) }}
                </router-link>
              </h4>

              <!-- Tour Details (Duration & Price) -->
              <div class="tour-details">
                <div v-if="widget.showDuration !== false && tour.duration_days" class="tour-duration">
                  <i class="bi bi-clock me-1"></i>
                  {{ tour.duration_days }} {{ tour.duration_days === 1 ? 'ημέρα' : 'ημέρες' }}
                </div>
                <div v-if="widget.showPrice !== false" class="tour-price">
                  <span v-if="tour.sale_price" class="sale-price">€{{ tour.sale_price }}</span>
                  <span class="regular-price" :class="{ 'strikethrough': tour.sale_price }">
                    €{{ tour.price }}
                  </span>
                </div>
              </div>

              <!-- Tour Meta (Departure Date & Category) -->
              <div class="tour-meta-info">
                <div v-if="tour.departure_date" class="tour-departure">
                  <i class="bi bi-calendar-event me-1"></i>
                  <span class="small">{{ formatDate(tour.departure_date) }}</span>
                </div>
                <div v-if="widget.showCategory !== false && tour.category_name" class="tour-category-text">
                  <i class="bi bi-tag me-1"></i>
                  <span class="small">{{ tour.category_name }}</span>
                </div>
              </div>

              <!-- Action Button -->
              <div class="tour-footer">
                <router-link 
                  v-if="widget.showBookButton !== false"
                  :to="`/tours/${tour.slug}`" 
                  class="btn btn-primary btn-sm w-100"
                >
                  <i class="bi bi-info-circle me-1"></i>
                  Λεπτομέρειες
                </router-link>
              </div>
            </div>
          </article>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="empty-state text-center py-4">
      <i class="bi bi-bus-front display-4 text-muted mb-3"></i>
      <h5 class="text-muted">Δεν βρέθηκαν εκδρομές</h5>
      <p class="text-muted mb-0">
        {{ widget.categoryId || widget.destinationId ? 'Δεν υπάρχουν εκδρομές με τα επιλεγμένα φίλτρα.' : 'Δεν υπάρχουν διαθέσιμες εκδρομές.' }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import type { TourCardsWidget } from '@/types/pagebuilder'
import { API_BASE_URL } from '@/utils/config'
import { authFetch } from '@/utils/authFetch'
import { UPLOADS_BASE_URL } from '@/config/env'
import { settingsService } from '@/services/api'
import { useWidgetStyles } from '@/composables/useWidgetStyles'

interface Props {
  widget: TourCardsWidget
}

interface Tour {
  id: number
  title: string
  slug: string
  short_description: string
  description: string
  featured_image: string
  category_id: number
  category_name: string
  category_slug: string
  destination_id: number
  destination_name: string
  destination_slug: string
  price: number
  sale_price: number | null
  duration: string
  duration_days: number
  departure_date: string | null
  created_at: string
  available_seats: number
  total_seats: number
  occupied_seats: number
  reserved_seats: number
  status: string
}

const props = defineProps<Props>()
const { widgetStyles, widgetClasses } = useWidgetStyles(props.widget)

// State
const tours = ref<Tour[]>([])
const loading = ref(false)
const navSettings = ref<any>({})

// Computed styles from navigation settings
const tooltipBgColor = computed(() => navSettings.value.navigation?.backgroundColor || '#3b82f6')
const tooltipTextColor = computed(() => navSettings.value.navigation?.textColor || '#ffffff')

// Load navigation settings
const loadNavSettings = async () => {
  try {
    const response = await settingsService.getNavigationSettings()
    if (response.success && response.data) {
      navSettings.value = response.data
    }
  } catch (error) {
    console.error('Error loading navigation settings:', error)
  }
}

// Methods
const loadTours = async () => {
  loading.value = true
  try {
    const params = new URLSearchParams({
      limit: props.widget.limit.toString(),
      status: 'published'
    })

    // If categoryId is provided, we need to fetch the slug first
    if (props.widget.categoryId) {
      // Fetch all categories to find the slug
      const categoriesResponse = await authFetch(`${API_BASE_URL}/tours.php?action=categories`)
      const categoriesData = await categoriesResponse.json()
      
      if (categoriesData.success && Array.isArray(categoriesData.data)) {
        const category = categoriesData.data.find((cat: any) => cat.id === Number(props.widget.categoryId))
        if (category && category.slug) {
          params.append('category', category.slug)
        }
      }
    }

    if (props.widget.destinationId) {
      // Fetch all destinations to find the slug
      const destinationsResponse = await authFetch(`${API_BASE_URL}/tours.php?action=destinations`)
      const destinationsData = await destinationsResponse.json()
      
      if (destinationsData.success && Array.isArray(destinationsData.data)) {
        const destination = destinationsData.data.find((dest: any) => dest.id === Number(props.widget.destinationId))
        if (destination && destination.slug) {
          params.append('destination', destination.slug)
        }
      }
    }

    if (props.widget.showFeaturedOnly) {
      params.append('featured', 'true')
    }

    const response = await authFetch(`${API_BASE_URL}/tours.php?${params}`)
    const data = await response.json()

    if (data.success && Array.isArray(data.data)) {
      // Sort tours by departure_date (nearest first)
      tours.value = data.data.sort((a: Tour, b: Tour) => {
        // Tours without departure_date go to the end
        if (!a.departure_date && !b.departure_date) return 0
        if (!a.departure_date) return 1
        if (!b.departure_date) return -1
        
        const dateA = new Date(a.departure_date)
        const dateB = new Date(b.departure_date)
        return dateA.getTime() - dateB.getTime()
      })
    } else {
      tours.value = []
    }
  } catch (error) {
    tours.value = []
  } finally {
    loading.value = false
  }
}

const getTourImageUrl = (image: string | null) => {
  if (!image) return `${API_BASE_URL}/placeholder.php?width=400&height=300&text=No Image&bg=e9ecef&color=6c757d`
  if (image.startsWith('http')) return image
  return `${UPLOADS_BASE_URL}/tours/${image}`
}

const handleImageError = (event: Event) => {
  const target = event.target as HTMLImageElement
  target.src = `${API_BASE_URL}/placeholder.php?width=400&height=300&text=Image Error&bg=f8d7da&color=721c24`
}

const formatPrice = (price: number | null) => {
  if (!price) return 'Τιμή κατόπιν συνεννόησης'
  return `€${price.toFixed(2)}`
}

// Helper to strip HTML tags
const stripHtml = (html: string | null) => {
  if (!html) return ''
  const tmp = document.createElement('DIV')
  tmp.innerHTML = html
  return tmp.textContent || tmp.innerText || ''
}

const formatDate = (dateString: string): string => {
  if (!dateString) return ''
  const date = new Date(dateString)
  const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' }
  return date.toLocaleDateString('el-GR', options)
}

const getAvailabilityClass = (tour: Tour) => {
  const available = tour.available_seats
  const total = tour.total_seats
  
  if (available === 0) return 'sold-out'
  if (available <= total * 0.2) return 'limited'
  return 'available'
}

const getAvailabilityText = (tour: Tour) => {
  const available = tour.available_seats
  const total = tour.total_seats
  
  if (available === 0) return 'Sold Out'
  if (available <= total * 0.2) return `Μόνο ${available} θέσεις!`
  return `${available} θέσεις`
}

// Wishlist functionality
const wishlistItems = ref<number[]>(JSON.parse(localStorage.getItem('tour_wishlist') || '[]'))

const isInWishlist = (tourId: number) => {
  return wishlistItems.value.includes(tourId)
}

const addToWishlist = (tour: Tour) => {
  const index = wishlistItems.value.indexOf(tour.id)
  if (index > -1) {
    // Remove from wishlist
    wishlistItems.value.splice(index, 1)
  } else {
    // Add to wishlist
    wishlistItems.value.push(tour.id)
  }
  localStorage.setItem('tour_wishlist', JSON.stringify(wishlistItems.value))
  
  // Dispatch custom event for other components to listen
  window.dispatchEvent(new CustomEvent('tour-wishlist-updated', { 
    detail: { tourId: tour.id, inWishlist: wishlistItems.value.includes(tour.id) }
  }))
}

// Quick view functionality
const openQuickView = (tour: Tour) => {
  // Create quick view modal content
  const modal = document.createElement('div')
  modal.className = 'tour-quick-view-modal'
  modal.innerHTML = `
    <div class="tour-quick-view-overlay" onclick="closeTourQuickView()">
      <div class="tour-quick-view-content" onclick="event.stopPropagation()">
        <button class="tour-quick-view-close" onclick="closeTourQuickView()">
          <i class="bi bi-x-lg"></i>
        </button>
        <div class="tour-quick-view-body">
          ${tour.featured_image ? `
            <div class="tour-quick-view-image">
              <img src="${getTourImageUrl(tour.featured_image)}" alt="${stripHtml(tour.title)}" />
            </div>
          ` : ''}
          <div class="tour-quick-view-info">
            <div class="d-flex align-items-center gap-2 mb-3">
              ${tour.category_name ? `
                <div class="tour-category-badge">
                  ${tour.category_name}
                </div>
              ` : ''}
            </div>
            <h3 class="tour-quick-view-title">${stripHtml(tour.title)}</h3>
            <div class="tour-quick-view-meta mb-3">
              ${tour.destination_name ? `
                <span class="tour-destination">
                  <i class="bi bi-geo-alt me-1"></i>
                  ${tour.destination_name}
                </span>
              ` : ''}
              ${tour.duration ? `
                <span class="tour-duration">
                  <i class="bi bi-clock me-1"></i>
                  ${tour.duration}
                </span>
              ` : ''}
            </div>
            <p class="tour-quick-view-excerpt">${stripHtml(tour.short_description) || 'Δεν υπάρχει περιγραφή διαθέσιμη.'}</p>
            ${tour.price ? `
              <div class="tour-quick-view-price mb-3">
                ${tour.sale_price ? `
                  <span class="original-price me-2">${formatPrice(tour.price)}</span>
                ` : ''}
                <span class="current-price">${formatPrice(tour.sale_price || tour.price)}</span>
              </div>
            ` : ''}
            <div class="tour-quick-view-actions">
              <a href="/tours/${tour.slug}" class="btn btn-primary">
                <i class="bi bi-calendar-check me-2"></i>
                Κράτηση Εκδρομής
              </a>
              <button onclick="addToWishlistFromModal(${tour.id})" class="btn btn-outline-secondary">
                <i class="bi bi-heart me-2"></i>
                ${isInWishlist(tour.id) ? 'Αφαίρεση από Wishlist' : 'Προσθήκη στη Wishlist'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  `
  
  document.body.appendChild(modal)
  
  // Add global functions for modal
  ;(window as any).closeTourQuickView = () => {
    document.body.removeChild(modal)
  }
  
  ;(window as any).addToWishlistFromModal = (tourId: number) => {
    const tour = tours.value.find(t => t.id === tourId)
    if (tour) {
      addToWishlist(tour)
      // Update button text in modal
      const button = modal.querySelector('.btn-outline-secondary')
      if (button) {
        button.innerHTML = `
          <i class="bi bi-heart me-2"></i>
          ${isInWishlist(tourId) ? 'Αφαίρεση από Wishlist' : 'Προσθήκη στη Wishlist'}
        `
      }
    }
  }
  
  // Close on escape key
  const handleEscape = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      ;(window as any).closeTourQuickView()
      document.removeEventListener('keydown', handleEscape)
    }
  }
  document.addEventListener('keydown', handleEscape)
}

// Lifecycle
onMounted(() => {
  loadNavSettings()
  loadTours()
})
</script>

<style scoped>
.tour-cards-widget {
  width: 100%;
}

/* CSS Grid layout for tour cards */
.tours-grid-layout {
  display: grid;
  grid-template-columns: repeat(var(--tour-columns, 3), 1fr);
  gap: 1.5rem;
}

.tour-grid-item {
  min-width: 0; /* Prevent grid blowout */
}

.widget-title {
  font-size: 1.4rem;
  font-weight: 600;
  margin-bottom: 0;
  color: #2c3e50;
}

.tour-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: visible;
  border: 1px solid rgba(0,0,0,0.08);
  position: relative;
}

.tour-card:hover {
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
  transform: scale(1.05);
  z-index: 10;
}

.tour-card:hover .tour-title {
  white-space: normal;
  overflow: visible;
  background: v-bind(tooltipBgColor);
  color: white;
  padding: 0.5rem;
  margin: -0.5rem -0.5rem 0.5rem -0.5rem;
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(59, 130, 246, 0.3);
  animation: titlePop 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

@keyframes titlePop {
  0% {
    opacity: 0;
    transform: scale(0.8) translateY(-10px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* Image Container */
.tour-image-wrapper {
  position: relative;
  width: 100%;
  padding-top: 100%; /* 1:1 Aspect Ratio - Square */
  overflow: hidden;
  background: #f8f9fa;
}

.tour-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.tour-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  transition: transform 0.3s ease;
  display: block;
}

.tour-card:hover .tour-image img {
  transform: scale(1.05);
}

.tour-category-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  background: rgba(13, 110, 253, 0.9);
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
  backdrop-filter: blur(10px);
  z-index: 2;
}

/* Content */
.tour-content {
  padding: 1rem 1.5rem 1.5rem 1.5rem;
  display: flex;
  flex-direction: column;
}

.tour-title {
  font-size: 0.85rem;
  transition: all 0.3s ease;
  font-weight: 600;
  margin-bottom: 0.5rem;
  line-height: 1.4;
  color: #2c3e50;
  transition: color 0.3s ease;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  position: relative;
  cursor: pointer;
}

.tour-title::after {
  content: attr(title);
  position: absolute;
  bottom: calc(100% + 15px);
  left: 50%;
  transform: translateX(-50%) scale(0.8);
  background: v-bind(tooltipBgColor);
  color: v-bind(tooltipTextColor);
  padding: 12px 20px;
  border-radius: 15px;
  font-size: 0.9rem;
  white-space: normal;
  max-width: 280px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3),
              0 0 0 1px rgba(255, 255, 255, 0.3) inset;
  opacity: 0;
  pointer-events: none;
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  z-index: 1000;
  line-height: 1.4;
  text-align: center;
  font-weight: 500;
  backdrop-filter: blur(10px);
}

.tour-title::before {
  content: '';
  position: absolute;
  bottom: calc(100% + 5px);
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-top: 10px solid v-bind(tooltipBgColor);
  opacity: 0;
  transition: opacity 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  z-index: 999;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2));
}

.tour-card:hover .tour-title::after {
  opacity: 1;
  transform: translateX(-50%) scale(1);
  animation: tooltipBounce 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.tour-card:hover .tour-title::before {
  opacity: 1;
}

@keyframes tooltipBounce {
  0% {
    opacity: 0;
    transform: translateX(-50%) scale(0.8) translateY(10px);
  }
  50% {
    transform: translateX(-50%) scale(1.05) translateY(-2px);
  }
  100% {
    opacity: 1;
    transform: translateX(-50%) scale(1) translateY(0);
  }
}

.tour-title-link {
  color: inherit;
  text-decoration: none;
  transition: color 0.3s ease;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tour-title-link:hover {
  color: #0d6efd;
  text-decoration: none;
}

.tour-details {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
}

.tour-duration {
  color: #6c757d;
  display: flex;
  align-items: center;
}

.tour-price {
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.sale-price {
  color: #dc3545;
}

.regular-price.strikethrough {
  text-decoration: line-through;
  color: #999;
  font-weight: normal;
}

.tour-meta-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0;
  margin-bottom: 0.75rem;
  border-top: 1px solid #f0f0f0;
  font-size: 0.85rem;
}

.tour-departure,
.tour-category-text {
  display: flex;
  align-items: center;
  color: #6c757d;
  gap: 0.25rem;
}

.tour-departure i,
.tour-category-text i {
  color: #0d6efd;
  font-size: 0.9rem;
}

.tour-excerpt {
  color: #6c757d;
  font-size: 0.9rem;
  line-height: 1.5;
  margin-bottom: 1rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-clamp: 2;
  overflow: hidden;
}

.tour-footer {
  margin-top: auto;
}

.original-price {
  font-size: 0.85rem;
  color: #6c757d;
  text-decoration: line-through;
}

.current-price {
  font-size: 1.25rem;
  font-weight: 700;
  color: #28a745;
}

/* Action Buttons Overlay */
.tour-actions-overlay {
  position: absolute;
  bottom: 12px;
  right: 12px;
  display: flex;
  gap: 8px;
  opacity: 0;
  transform: translateY(10px);
  transition: all 0.3s ease;
  z-index: 2;
}

.tour-card:hover .tour-actions-overlay {
  opacity: 1;
  transform: translateY(0);
}

.action-btn {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  backdrop-filter: blur(10px);
}

.wishlist-btn {
  background: rgba(220, 53, 69, 0.9);
  color: white;
}

.wishlist-btn:hover {
  background: #dc3545;
  transform: scale(1.1) rotate(5deg);
}

.wishlist-btn.active {
  background: #dc3545;
  animation: pulse-heart 0.6s ease;
}

@keyframes pulse-heart {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.2); }
}

.quick-view-btn {
  background: rgba(13, 110, 253, 0.9);
  color: white;
}

.quick-view-btn:hover {
  background: #0d6efd;
  transform: scale(1.1) rotate(-5deg);
}

/* Loading and Empty States */
.loading-state,
.empty-state {
  padding: 3rem 1rem;
}

.loading-state .spinner-border {
  color: #0d6efd;
}

/* Quick View Modal Styles */
:global(.tour-quick-view-modal) {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 10000;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

:global(.tour-quick-view-overlay) {
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

:global(.tour-quick-view-content) {
  background: white;
  border-radius: 20px;
  max-width: 900px;
  width: 100%;
  max-height: 80vh;
  overflow: hidden;
  position: relative;
  animation: slideUp 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

@keyframes slideUp {
  from { 
    opacity: 0;
    transform: translateY(50px) scale(0.9);
  }
  to { 
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

:global(.tour-quick-view-close) {
  position: absolute;
  top: 15px;
  right: 15px;
  width: 40px;
  height: 40px;
  border: none;
  background: rgba(0,0,0,0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: all 0.3s ease;
}

:global(.tour-quick-view-close:hover) {
  background: rgba(220, 53, 69, 0.9);
  color: white;
  transform: scale(1.1);
}

:global(.tour-quick-view-body) {
  display: flex;
  max-height: 80vh;
  overflow: hidden;
}

:global(.tour-quick-view-image) {
  flex: 0 0 350px;
  height: 450px;
}

:global(.tour-quick-view-image img) {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

:global(.tour-quick-view-info) {
  flex: 1;
  padding: 2rem;
  overflow-y: auto;
}

:global(.tour-quick-view-title) {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: #2c3e50;
}

:global(.tour-quick-view-meta) {
  display: flex;
  gap: 1.5rem;
  font-size: 0.95rem;
  color: #6c757d;
}

:global(.tour-quick-view-excerpt) {
  color: #6c757d;
  line-height: 1.6;
  margin-bottom: 1.5rem;
}

:global(.tour-quick-view-price) {
  font-size: 1.5rem;
  font-weight: 700;
}

:global(.tour-quick-view-price .original-price) {
  font-size: 1.1rem;
  color: #6c757d;
  text-decoration: line-through;
}

:global(.tour-quick-view-price .current-price) {
  color: #28a745;
}

:global(.tour-quick-view-actions) {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .tours-grid-layout {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }

  .tours-grid {
    padding: 0;
    margin: 0 auto;
  }
  
  .tour-card {
    margin-bottom: 0;
  }
  
  .tour-content {
    padding: 1rem;
  }
  
  .widget-title {
    font-size: 1.2rem;
  }
  
  .tour-image {
    height: 180px;
  }
  
  .tour-title {
    font-size: 0.95rem;
    margin-bottom: 0.4rem;
  }
  
  .tour-details {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.3rem;
    margin-bottom: 0.4rem;
  }
  
  .tour-duration {
    font-size: 0.75rem;
  }
  
  .tour-price {
    font-size: 0.85rem;
  }
  
  .tour-meta-info {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.3rem;
    padding: 0.4rem 0;
  }
  
  .tour-departure,
  .tour-category-text {
    font-size: 0.75rem;
  }
  
  :global(.tour-quick-view-overlay) {
    padding: 1rem;
  }
  
  :global(.tour-quick-view-body) {
    flex-direction: column;
  }
  
  :global(.tour-quick-view-image) {
    flex: none;
    height: 200px;
  }
  
  :global(.tour-quick-view-info) {
    padding: 1.5rem;
  }
  
  :global(.tour-quick-view-actions) {
    flex-direction: column;
  }
}

@media (max-width: 576px) {
  .tour-content {
    padding: 0.875rem;
  }
  
  .tour-image {
    height: 160px;
  }
  
  .tour-title {
    font-size: 0.95rem;
  }
  
  .tour-excerpt {
    font-size: 0.85rem;
    -webkit-line-clamp: 2;
    line-clamp: 2;
  }
  
  .btn-sm {
    font-size: 0.75rem !important;
    padding: 0.25rem 0.5rem !important;
  }
}
</style>
